import { ArchivingSystemProperties } from '@app/modules/configuration/models/configuration.model';
import { ContractStatusEnum }        from '@app/modules/contract/models/contract.model';
import { ErsStatusStatistics }       from '@app/modules/ers/models/ers.model';
import {
	TssModeStatistics,
	TssStatusStatistics
}                                    from '@app/modules/tss/models/tss.model';
import { RequestState }              from '@core/interfaces/api.interface';
import { IAdminTenantEntityEvents }  from '@core/interfaces/entity.interface';
import { Address }                   from '@shared/components/address/models/address.model';


// ENUMS #############################################################
// API enums *********************************************************
export enum TenantGroupEnum {
	DeutscheFiskal = 'df'
}

export enum TenantStatusEnum {
	ACTIVE = 'active',
	DELETED = 'deleted',
	DISABLED = 'disabled',
	SUSPENDED = 'suspended'
}

export enum TenantTypeEnum {
	ADMIN = 'admin',
	DEVELOPMENT = 'development',
	DISTRIBUTOR = 'distributor',
	INTERNAL = 'internal',
	MANAGED = 'managed',
	PARTNER = 'partner',
	RESELLER = 'reseller',
	STANDARD = 'standard',
	TEST = 'test',
	TRIAL = 'trial'
}

// UI enums *********************************************************
export enum CapabilitiesEnum {
	ARCHIVING = 'archiving'
}


// INTERFACES ########################################################
// API interfaces ****************************************************
// UI interfaces *****************************************************
interface ITenantFormBase {
	additionalData?: string;
	archivingContract?: TenantContractLog;
	billingAddress?: Address;
	capabilities?: CapabilitiesEnum[];
	customerIdentifier?: string;
	detailedDescription?: string;
	elsterContract?: TenantContractLog;
	fiscalContract?: TenantContractLog;
	name?: string;
	systemProperties?: SystemProperties;
	tenantGroup?: TenantGroupEnum;
	tenantType?: TenantTypeEnum;
	vendorCode?: string;
}

interface ITenantInitialization {
	email?: string;
	firstname?: string;
	lastname?: string;
	phoneNumber?: string;
	taxNumber?: string;
	userRoles?: string[];
	vatId?: string;
}

interface ITenantBody {
	parentTenantId?: string;
}

export interface ITenantForm extends ITenantFormBase, ITenantInitialization, ITenantBody {}


// TYPES #############################################################
export type TenantProp = ( keyof Tenant );
export type TenantProps = TenantProp[];

// ENTITIES ##########################################################
// MODEL USED FOR TRANSITION APP<->API (TransitionalModelObject)
class TenantFormBaseTMO implements ITenantFormBase {
	public additionalData?: string = undefined;
	public archivingContract?: TenantContractLog = undefined;
	public billingAddress?: Address = new Address();
	public capabilities?: CapabilitiesEnum[] = [];
	public customerIdentifier?: string = undefined;
	public detailedDescription: string = undefined;
	public elsterContract?: TenantContractLog = undefined;
	public fiscalContract?: TenantContractLog = undefined;
	public name: string = undefined;
	public systemProperties?: SystemProperties = undefined;
	public tenantGroup?: TenantGroupEnum = undefined;
	public tenantType?: TenantTypeEnum = undefined;
	public vendorCode?: string = undefined;
}

export class TenantInitializationTMO extends TenantFormBaseTMO implements ITenantInitialization {
	public email: string = undefined;
	public firstname: string = undefined;
	public lastname: string = undefined;
	public phoneNumber?: string = undefined;
	public taxNumber?: string = undefined;
	public userRoles?: string[] = [];
	public vatId?: string = undefined;
}

export class TenantFormBodyTMO extends TenantFormBaseTMO implements ITenantBody {
	public parentTenantId: string = undefined;
}

export class TenantTMO {
	public additionalData?: string = undefined;
	public archivingContractLog?: TenantContractLog[] = undefined;
	public billingAddress: Address = new Address();
	public createdBy?: string = undefined;
	public createdDate?: Date = undefined;
	public customerIdentifier?: string = undefined;
	public detailedDescription: string = undefined;
	public elsterContractLog?: TenantContractLog[] = undefined;
	public fiscalContractLog?: TenantContractLog[] = undefined;
	public id: string = undefined;
	public lastModifiedBy?: string = undefined;
	public lastModifiedDate?: Date = undefined;
	public name: string = undefined;
	public parentTenantId?: string = undefined;
	public status: TenantStatusEnum = undefined;
	public systemProperties?: SystemProperties = new SystemProperties();
	public tenantGroup?: TenantGroupEnum = undefined;
	public tenantType?: TenantTypeEnum = undefined;
	public vendorCode?: string = undefined;
}

export class TenantContractLogTMO {
	public contractId?: string = undefined;
	public from?: Date | string = undefined;
	public to?: Date | string = undefined;
}

export class TenantStatisticsTMO {
	public businessPremises?: number = undefined;
	public ers?: ErsStatusStatistics = new ErsStatusStatistics();
	public organizations?: number = undefined;
	public tss?: TssStatusStatistics = new TssStatusStatistics();
	public tssPerMode?: TssModeStatistics = new TssModeStatistics();
}

export class SystemPropertiesTMO {
	public archiving?: ArchivingSystemProperties = undefined;
}


// MODEL USED IN APPLICATION (with additional properties)
export class Tenant extends TenantTMO {
	public capabilities?: CapabilitiesEnum[] = [];
	public etag?: string = undefined;
	public parentTenantName?: string = undefined;
	public showPlainEntity?: boolean = undefined;
	public ui?: IAdminTenantEntityEvents = {
		requestState: RequestState.NOT_ASKED,
		requestError: undefined,
		entityActions: {}
	};
}

export class TenantContractLog extends TenantContractLogTMO {
	public from: Date = undefined;
	public name: string = undefined;
	public status: ContractStatusEnum = undefined;
	public to: Date = undefined;
}

export class TenantStatistics extends TenantStatisticsTMO {}

export class TenantInitialization extends TenantInitializationTMO {
	public etag?: string = undefined;
	public id?: string = undefined;
}

export class TenantFormBody extends TenantFormBodyTMO {}

export type TenantFormType = ( keyof ITenantForm );

export class TenantForm extends TenantFormBaseTMO implements ITenantForm {
	public etag?: string = undefined;
	public id?: string = undefined;
	public parentTenantId?: string = undefined;
	public phoneNumber?: string = undefined;
	public taxNumber?: string = undefined;
	public userRoles?: string[] = [];
	public vatId?: string = undefined;
}


export class SystemProperties extends SystemPropertiesTMO {}

